import { createFileRoute, Link } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { FeatureGate } from "@/components/FeatureGate";
import { useEffect, useMemo, useState } from "react";
import {
  addDays,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  parseISO,
  startOfWeek,
  subWeeks,
} from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { BarChart3, CalendarDays, CheckCircle2, Clock, FileText, Link2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_app/growth")({
  component: () => (
    <FeatureGate feature="content">
      <GrowthAnalyticsPage />
    </FeatureGate>
  ),
  head: () => ({
    meta: [
      { title: "Growth Analytics · AdKavi" },
      { name: "description", content: "Track your content publishing activity and growth." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Post = {
  id: string;
  platform: string;
  kind: string;
  status: string;
  scheduled_at: string;
  title: string;
};

const PLATFORM_COLORS: Record<string, string> = {
  instagram: "bg-pink-500",
  facebook: "bg-blue-500",
  tiktok: "bg-orange-500",
  linkedin: "bg-sky-500",
  twitter: "bg-zinc-400",
  youtube: "bg-red-500",
};

const PLATFORM_LABELS: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  twitter: "X / Twitter",
  youtube: "YouTube",
};

function GrowthAnalyticsPage() {
  const { t } = useI18n();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const now = useMemo(() => new Date(), []);

  useEffect(() => {
    let cancelled = false;
    const since = subWeeks(now, 8).toISOString();
    const until = addDays(now, 30).toISOString();
    supabase
      .from("scheduled_posts")
      .select("id,platform,kind,status,scheduled_at,title")
      .gte("scheduled_at", since)
      .lte("scheduled_at", until)
      .order("scheduled_at", { ascending: true })
      .then(({ data }) => {
        if (cancelled) return;
        setPosts((data ?? []) as Post[]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [now]);

  const thisMonth = useMemo(
    () => posts.filter((p) => isSameMonth(parseISO(p.scheduled_at), now)),
    [posts, now],
  );

  const stats = useMemo(() => ({
    total: thisMonth.length,
    published: thisMonth.filter((p) => p.status === "published").length,
    scheduled: thisMonth.filter((p) => p.status === "scheduled").length,
    drafts: thisMonth.filter((p) => p.status === "draft").length,
  }), [thisMonth]);

  const weeklyData = useMemo(() => {
    return Array.from({ length: 8 }, (_, i) => {
      const weekStart = startOfWeek(subWeeks(now, 7 - i), { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);
      const label = format(weekStart, "MMM d");
      const count = posts.filter((p) => {
        const d = parseISO(p.scheduled_at);
        return !isBefore(d, weekStart) && !isAfter(d, weekEnd);
      }).length;
      return { label, count };
    });
  }, [posts, now]);

  const platformCounts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const p of posts) {
      map[p.platform] = (map[p.platform] ?? 0) + 1;
    }
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [posts]);

  const maxPlatformCount = useMemo(
    () => Math.max(1, ...platformCounts.map(([, n]) => n)),
    [platformCounts],
  );

  const upcoming = useMemo(
    () =>
      posts
        .filter((p) => isAfter(parseISO(p.scheduled_at), now) && isBefore(parseISO(p.scheduled_at), addDays(now, 7)))
        .slice(0, 8),
    [posts, now],
  );

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <BarChart3 className="w-7 h-7 text-orange-400 shrink-0" />
          <span className="bg-gradient-to-r from-orange-400 to-primary bg-clip-text text-transparent">{t("growth.title")}</span>
        </h1>
        <p className="mt-1.5 text-muted-foreground text-sm">{t("growth.subtitle")}</p>
      </div>

      {/* Stat cards — always shown; values start at 0 and update when data loads */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label={t("growth.stat.thisMonth")} value={stats.total} icon={<FileText className="w-4 h-4" />} />
        <StatCard label={t("growth.stat.published")} value={stats.published} icon={<CheckCircle2 className="w-4 h-4" />} accent="text-emerald-400" />
        <StatCard label={t("growth.stat.scheduled")} value={stats.scheduled} icon={<Clock className="w-4 h-4" />} accent="text-primary" />
        <StatCard label={t("growth.stat.drafts")} value={stats.drafts} icon={<CalendarDays className="w-4 h-4" />} accent="text-muted-foreground" />
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Posts per week chart */}
        <div className="lg:col-span-3 rounded-2xl border border-border bg-card p-5">
          <div className="mb-4">
            <p className="font-semibold text-sm">{t("growth.chart.weekly")}</p>
            <p className="text-xs text-muted-foreground">{t("growth.chart.weeklySub")}</p>
          </div>
          {loading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <div className="relative">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={weeklyData} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    axisLine={false}
                    tickLine={false}
                    width={24}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    cursor={{ fill: "hsl(var(--muted)/0.4)" }}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              {weeklyData.every((d) => d.count === 0) && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-xs text-muted-foreground/60 text-center max-w-[220px] leading-relaxed">
                    No posts yet — start scheduling to see your activity
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Platform breakdown */}
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
          <p className="font-semibold text-sm mb-4">{t("growth.chart.platforms")}</p>
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : platformCounts.length === 0 ? (
            <p className="text-xs text-muted-foreground">No posts yet.</p>
          ) : (
            <div className="space-y-3">
              {platformCounts.map(([platform, count]) => (
                <div key={platform} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="font-medium">{PLATFORM_LABELS[platform] ?? platform}</span>
                    <span className="text-muted-foreground tabular-nums">{count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={cn("h-full rounded-full transition-all", PLATFORM_COLORS[platform] ?? "bg-primary")}
                      style={{ width: `${(count / maxPlatformCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upcoming 7 days */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="font-semibold text-sm mb-4">{t("growth.upcoming.title")}</p>
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : upcoming.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("growth.upcoming.empty")}</p>
        ) : (
          <div className="divide-y divide-border">
            {upcoming.map((p) => {
              const date = parseISO(p.scheduled_at);
              return (
                <div key={p.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <div className="text-center min-w-[40px]">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{format(date, "EEE")}</div>
                    <div className="text-lg font-bold tabular-nums leading-tight">{format(date, "d")}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {PLATFORM_LABELS[p.platform] ?? p.platform} · {format(date, "h:mm a")}
                    </p>
                  </div>
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full shrink-0",
                      PLATFORM_COLORS[p.platform] ?? "bg-primary",
                    )}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Connect accounts banner */}
      <div className="rounded-2xl border border-border bg-muted/30 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <Link2 className="w-8 h-8 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{t("growth.connect.title")}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("growth.connect.desc")}</p>
        </div>
        <Button asChild size="sm" className="shrink-0 bg-orange-500 hover:bg-orange-600 text-white border-0">
          <Link to="/settings">{t("growth.connect.cta")}</Link>
        </Button>
      </div>
    </section>
  );
}

function StatCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground mb-1">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className={cn("text-2xl font-bold tabular-nums", accent)}>{value}</div>
    </div>
  );
}
